import { Icon } from '../icons/Icon'
import { Dialog } from '../popups/Dialog'
import { usePackOptimaModal } from './PackOptimaModalContext'

const allowedDiscounts = [2000, 1500, 1000] as const

const normalizeDiscountAmount = (
  amount?: number | null,
): number | undefined => {
  if (!amount) return undefined
  const value = Number(amount)
  if (!Number.isFinite(value) || value <= 0) return undefined

  return allowedDiscounts.reduce((closest, current) => {
    const deltaCurrent = Math.abs(current - value)
    const deltaClosest = Math.abs(closest - value)
    return deltaCurrent < deltaClosest ? current : closest
  }, allowedDiscounts[0])
}

type PackOptimaBadgeProps = {
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  className?: string
  size?: 'small' | 'medium' | 'large'
  text?: string
}

export function PackOptimaBadge({
  onClick,
  className = '',
  size = 'medium',
  text = 'Pack Óptima',
}: PackOptimaBadgeProps) {
  const sizeClasses = {
    small: 'h-[20px] gap-0.5 px-1',
    medium: 'h-7 gap-[3px] px-2',
    large: 'h-8 gap-1 px-3',
  }

  const iconSizes = {
    small: '12px' as const,
    medium: '16px' as const,
    large: '16px' as const,
  }

  const textClasses = {
    small: 'text-subtitle2-medium leading-[8px]',
    medium: 'text-subtitle1-medium',
    large: 'text-base-medium',
  }

  return (
    <button
      onClick={(e) => onClick?.(e)}
      className={`
        flex cursor-pointer items-center rounded bg-primary-50 transition-all

        hover:bg-primary-100

        ${sizeClasses[size]}
        ${className}
      `}
      type="button"
    >
      <Icon name="basic/shield-check" size={iconSizes[size]} color="#01a8b2" />
      <span
        className={`
          text-nowrap text-primary-500

          ${textClasses[size]}
        `}
      >
        {text}
      </span>
    </button>
  )
}

type PackOptimaModalProps = {
  isOpen: boolean
  onClose: () => void
  discountAmount?: number | null
}

export function PackOptimaModal({
  isOpen,
  onClose,
  discountAmount,
}: PackOptimaModalProps) {
  const normalizedDiscount = normalizeDiscountAmount(discountAmount)
  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose()}
      variant="center"
      className="!max-w-[600px] !bg-white"
      containerClassName="!p-0"
      iconClassName="!text-neutral-900"
    >
      <div className="flex flex-col px-5 pb-5">
        <div className="px-6 pb-6 pt-8">
          <h2 className="mb-2 text-h4 font-bold text-neutral-900">
            Pack Óptima, tu mejor elección.
          </h2>
          <p className="text-base text-neutral-500">
            Financiando tu coche en 72 plazos o más, accedes a ventajas
            exclusivas:
          </p>
        </div>

        <div className="space-y-4 px-6 pb-4">
          {normalizedDiscount && normalizedDiscount > 0 && (
            <div className="flex items-start gap-3 border-b pb-2">
              <div className="flex-shrink-0">
                <Icon name="basic/check" size="16px" color="#01a8b2" />
              </div>
              <p className="text-base text-neutral-500">
                Descuento de {normalizedDiscount.toLocaleString('es-ES')}€
              </p>
            </div>
          )}

          <div className="flex items-start gap-3 border-b pb-2">
            <div className="flex-shrink-0">
              <Icon name="basic/check" size="16px" color="#01a8b2" />
            </div>
            <div>
              <p className="text-base text-neutral-500">
                Dos años de garantía para conducir con total tranquilidad.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 border-b pb-2">
            <div className="flex-shrink-0">
              <Icon name="basic/check" size="16px" color="#01a8b2" />
            </div>
            <p className="text-base text-neutral-500">
              Aceptamos tu coche como parte del pago
            </p>
          </div>

          <div className="flex items-start gap-3 border-b pb-2">
            <div className="flex-shrink-0">
              <Icon name="basic/check" size="16px" color="#01a8b2" />
            </div>
            <p className="text-base text-neutral-500">
              Vehículo de sustitución
            </p>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <Icon name="basic/check" size="16px" color="#01a8b2" />
            </div>
            <p className="text-base text-neutral-500">
              Seguro de vida, invalidez y retirada de carnet
            </p>
          </div>
        </div>

        <div className="space-y-2 px-6 py-2">
          <p className="text-base text-neutral-500">
            Con Pack Óptima, tu compra incluye mucho más que un coche.
          </p>
        </div>
      </div>
    </Dialog>
  )
}

type PackOptimaWithModalProps = {
  className?: string
  size?: 'small' | 'medium' | 'large'
  discountAmount?: number
}

export function PackOptimaWithModal({
  className,
  discountAmount,
  size = 'medium',
}: PackOptimaWithModalProps) {
  const { openModal } = usePackOptimaModal()
  return (
    <PackOptimaBadge
      onClick={(e) => {
        e?.stopPropagation()
        e?.preventDefault()
        openModal(discountAmount)
      }}
      className={className}
      size={size}
    />
  )
}

type PackOptimaWarningModalProps = {
  isOpen: boolean
  discountAmount?: number | null
  onKeepPackOptima: () => void
  onContinueWithoutBenefits: () => void
}

export function PackOptimaWarningModal({
  isOpen,
  discountAmount,
  onKeepPackOptima,
  onContinueWithoutBenefits,
}: PackOptimaWarningModalProps) {
  const normalizedDiscount = normalizeDiscountAmount(discountAmount)
  return (
    <Dialog
      open={isOpen}
      onClose={onKeepPackOptima}
      variant="center"
      className="!max-w-[600px] !bg-white"
      containerClassName="!p-0"
      hideIcon={true}
      closeOnOutsideClick={false}
    >
      <div className="flex flex-col px-8 py-8">
        <div className="mb-6">
          <h2 className="mb-4 text-h4 font-bold text-neutral-900">
            ¡No pierdas los beneficios del Pack Óptima!
          </h2>
          <p className="text-base text-neutral-500">
            Seleccionando una financiación menor a 72 plazos y/o una entrada
            inicial mayor al 50% del coche te pierdes los siguientes beneficios:
          </p>
        </div>

        <div className="mb-6 space-y-3">
          {normalizedDiscount && normalizedDiscount > 0 && (
            <div className="flex items-start gap-3 border-b pb-2">
              <div className="flex-shrink-0">
                <Icon name="basic/check" size="16px" color="#01a8b2" />
              </div>
              <p className="text-base text-neutral-500">
                Descuento de {normalizedDiscount.toLocaleString('es-ES')}€
              </p>
            </div>
          )}

          <div className="flex items-start gap-3 border-b pb-2">
            <div className="flex-shrink-0">
              <Icon name="basic/check" size="16px" color="#01a8b2" />
            </div>
            <div>
              <p className="text-base text-neutral-500">
                Dos años de garantía para conducir con total tranquilidad.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 border-b pb-2">
            <div className="flex-shrink-0">
              <Icon name="basic/check" size="16px" color="#01a8b2" />
            </div>
            <p className="text-base text-neutral-500">
              Aceptamos tu coche como parte del pago
            </p>
          </div>

          <div className="flex items-start gap-3 border-b pb-2">
            <div className="flex-shrink-0">
              <Icon name="basic/check" size="16px" color="#01a8b2" />
            </div>
            <p className="text-base text-neutral-500">
              Vehículo de sustitución
            </p>
          </div>

          <div className="flex items-start gap-3 border-b pb-2">
            <div className="flex-shrink-0">
              <Icon name="basic/check" size="16px" color="#01a8b2" />
            </div>
            <p className="text-base text-neutral-500">
              Seguro de vida, invalidez y retirada de carnet
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={onKeepPackOptima}
            className={`
              h-12 w-full rounded-lg bg-primary-500 text-base-bold text-white
              transition-colors

              hover:bg-primary-600
            `}
            type="button"
          >
            Quiero el Pack Óptima
          </button>

          <button
            onClick={onContinueWithoutBenefits}
            className={`
              h-12 w-full rounded-lg border border-neutral-200 bg-white
              text-base-bold text-neutral-700 transition-colors

              hover:bg-neutral-50
            `}
            type="button"
          >
            Continuar sin beneficios
          </button>
        </div>
      </div>
    </Dialog>
  )
}
